import React, { useEffect, useState, useCallback } from 'react';
import Icon from 'react-native-vector-icons/dist/Feather';
import { useTheme } from 'styled-components/native';
import { TouchableOpacity, View } from 'react-native';
import { useApi } from '~/hooks/api';
import { serializeString } from '~/utils/strings';
import { usePageWithCardSettings, useStepper } from '~/components/@tem-ui/Stepper';
import { useAppointment } from '~/hooks/appointment';
import { useTracking } from '~/services/tracking';
import { trackingEventType } from '~/contexts/tracking/types';
import { TelemedicineDoctor } from '~/contexts/types';
import { Input } from '~/components/@hello-ui/Input';
import { Typography } from '~/components/@hello-ui';
import { Listing } from '~/components/@hello-ui/Listing';
import { isPicpayOnix, PICPAY_ONIX } from '~/helpers/picpay';
import { useAuth } from '~/auth/legacy/useAuth';
import { PAGBANK_ONIX } from '~/helpers/pagbank';
import { DeepLink } from '~/auth/strategies/deep-link/deep-link-utils';
import { BMG_ONIX } from '~/helpers/bmg';

const BOT_SPECIALTY_ID = 2;

export const ScheduleTelemedicineDoctor = (): JSX.Element => {
  usePageWithCardSettings({ scrollEnabled: false });
  const api = useApi();
  const theme = useTheme();

  const { appointment, setAppointment } = useAppointment();
  const { stepper, nextStep } = useStepper();
  const { tracking } = useTracking();

  const [term, setTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [doctors, setDoctors] = useState<TelemedicineDoctor[]>([]);
  const [fullDoctors, setFullDoctors] = useState<TelemedicineDoctor[]>([]);
  const { isDeepLink } = DeepLink.useParams();

  const { onixCode } = useAuth();

  useEffect(() => {
    setAppointment({
      ...appointment,
      doctorId: null,
    });
  }, []);

  const sendMovementRecordAppointmentStatus = (doctor: TelemedicineDoctor) => {
    void tracking.addMovement({
      event: trackingEventType.ClickedOnSelectDoctorTelemedicine,
      metadata: {
        appointment: {
          ...doctor,
        },
      },
    });

    void setAppointment((prev) =>
      prev
        ? {
            ...prev,
            doctorId: doctor.id,
            doctorName: doctor.name,
          }
        : prev,
    );
  };

  const renderItem = ({ item }) => {
    return (
      <TouchableOpacity
        className="w-full flex-row py-24"
        onPress={() => {
          sendMovementRecordAppointmentStatus(item);
          nextStep();
        }}>
        <Typography variant="body2" color={theme.colors.black}>
          {item.name}
        </Typography>
        <View className="absolute right-0 top-24">
          <Icon name="chevron-right" size={24} color={theme?.colors?.primary} />
        </View>
      </TouchableOpacity>
    );
  };

  const getData = async () => {
    if (
      [...PICPAY_ONIX, ...PAGBANK_ONIX, ...BMG_ONIX].includes(
        onixCode?.onixCode.toString() || '',
      ) &&
      isDeepLink
    ) {
      setAppointment((prev) =>
        prev
          ? {
              ...prev,
              specialty: { id: String(BOT_SPECIALTY_ID), name: 'Clínica Geral' },
            }
          : prev,
      );
    }

    const { data } = await api.specialtyDoctors(
      [...PICPAY_ONIX, ...PAGBANK_ONIX, ...BMG_ONIX].includes(
        onixCode?.onixCode.toString() || '',
      ) && isDeepLink
        ? BOT_SPECIALTY_ID
        : appointment?.specialty?.id,
    );

    setDoctors(data.data);
    setFullDoctors(data.data);

    setLoading(false);
  };

  const handleSearch = useCallback(
    (text: string) => {
      const query: string = serializeString(text);
      const filteredData = fullDoctors.filter(({ name }: TelemedicineDoctor) =>
        serializeString(name).includes(query),
      );

      setDoctors(filteredData);
      setTerm(text);
    },
    [fullDoctors],
  );

  useEffect(() => {
    void getData();
  }, []);

  return (
    <>
      <View className="w-full max-w-[576px]">
        <Typography variant="title">Escolha o médico</Typography>
      </View>
      <View className="mt-24 mobile:mt-16">
        <Input
          variant="search"
          value={term}
          onChangeText={(value) => handleSearch(value)}
          label="Pesquisar"
          placeholder="Digite o nome"
        />
      </View>
      <Listing data={doctors} loading={loading} renderItem={renderItem} useSeparator={true} />
    </>
  );
};
